import { useEffect, useState, useCallback } from "react";
import {
    FaSearch,
    FaSpinner,
    FaPlane,
    FaUndo
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./DepartureFlights.css";

const API_URL = process.env.REACT_APP_API_URL;

function getTodayDateString() {

    const now = new Date();

    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

}

function formatDateTime(value) {

    if (!value) return "-";

    return new Date(value).toLocaleString();

}

function DepartureFlights() {

    const [generalSearch, setGeneralSearch] = useState("");

    const [flightType, setFlightType] = useState("");

    const [flightStatus, setFlightStatus] = useState("");

    const [fromDate, setFromDate] = useState(getTodayDateString());

    const [toDate, setToDate] = useState(getTodayDateString());

    const [statusOptions, setStatusOptions] = useState([]);

    const [flights, setFlights] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {

        loadStatusOptions();

        loadFlights();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadStatusOptions = async () => {

        try {

            const res = await fetch(`${API_URL}/portal/departure-flights/status-options`);

            const data = await res.json();

            if (data.status) {

                setStatusOptions(data.data || []);

            }

        } catch (err) {

            console.error("Failed to load status options:", err);

        }

    };

    const loadFlights = useCallback(async () => {

        setIsLoading(true);

        try {

            const params = new URLSearchParams();

            if (generalSearch) params.append("general_search", generalSearch);

            if (flightType) params.append("flight_type", flightType);

            if (flightStatus) params.append("flight_status", flightStatus);

            if (fromDate) params.append("from_date", fromDate);

            if (toDate) params.append("to_date", toDate);

            const res = await fetch(`${API_URL}/portal/departure-flights?${params.toString()}`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            setFlights(data || []);

        } catch (err) {

            console.error("Failed to load flights:", err);

            setFlights([]);

            toast.error("Unable to load flights. Please try again.");

        } finally {

            setIsLoading(false);

        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generalSearch, flightType, flightStatus, fromDate, toDate]);

    const handleReset = () => {

        const today = getTodayDateString();

        setGeneralSearch("");

        setFlightType("");

        setFlightStatus("");

        setFromDate(today);

        setToDate(today);

        setTimeout(loadFlights, 0);

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2><FaPlane /> Departure Flights</h2>
                    <p className="page-subtitle">
                        Search flight records synced from AODB. Read-only - flight data can't be
                        added or edited here, only searched and viewed.
                    </p>
                </div>

            </div>

            <div className="flight-filter-row">

                <input
                    placeholder="Search flight no, airline, destination, gate..."
                    value={generalSearch}
                    onChange={(e) => setGeneralSearch(e.target.value)}
                    className="flight-filter-general"
                />

                <select value={flightType} onChange={(e) => setFlightType(e.target.value)}>
                    <option value="">All Types</option>
                    <option value="A">Arrival (A)</option>
                    <option value="D">Departure (D)</option>
                </select>

                <select value={flightStatus} onChange={(e) => setFlightStatus(e.target.value)}>
                    <option value="">All Statuses</option>
                    {statusOptions.map(s => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>

                <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    title="From Date"
                />

                <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    title="To Date"
                />

                <button type="button" className="add-btn" onClick={loadFlights}>
                    <FaSearch /> Search
                </button>

                <button type="button" className="add-btn flight-reset-btn" onClick={handleReset}>
                    <FaUndo /> Reset
                </button>

            </div>

            <div className="table-container">

                <table>

                    <thead>

                        <tr>
                            <th>Type</th>
                            <th>Flight No</th>
                            <th>Airline</th>
                            <th>Destination</th>
                            <th>Terminal</th>
                            <th>Scheduled</th>
                            <th>Gate</th>
                            <th>Status</th>
                            <th>Delay (min)</th>
                            <th>Remarks</th>
                        </tr>

                    </thead>

                    <tbody>

                        {

                            isLoading ?

                                <tr>
                                    <td colSpan="10" style={{ textAlign: "center", padding: "40px" }}>
                                        <FaSpinner className="spin" /> Loading flights...
                                    </td>
                                </tr>

                                :

                                flights.length === 0 ?

                                    <tr>
                                        <td colSpan="10" style={{ textAlign: "center", padding: "40px" }}>
                                            No flights found for the selected filters.
                                        </td>
                                    </tr>

                                    :

                                    flights.map(item => (

                                        <tr key={item.id}>

                                            <td>
                                                <span className={`flight-type-badge flight-type-${(item.flight_type || "").toLowerCase()}`}>
                                                    {item.flight_type || "-"}
                                                </span>
                                            </td>

                                            <td>{item.flight_number || "-"}</td>

                                            <td>{item.airline_name || item.airline_code || "-"}</td>

                                            <td>{item.destination_name || item.destination_code || "-"}</td>

                                            <td>{item.terminal || "-"}</td>

                                            <td>{formatDateTime(item.scheduled_departure)}</td>

                                            <td>{item.boarding_gate || "-"}</td>

                                            <td>{item.flight_status || "-"}</td>

                                            <td>{item.delay_min != null ? item.delay_min : "-"}</td>

                                            <td className="flight-remarks-cell">{item.remarks || "-"}</td>

                                        </tr>

                                    ))

                        }

                    </tbody>

                </table>

            </div>

        </div>

    );

}

export default DepartureFlights;
